import { useEffect, useState } from 'react';
import { getOpinionFilterCond } from '@op/services/filter';
import {
  filterOptionsAssemble,
  getRequestFuncList,
  getRequestType,
} from '../utils';
import type { FilterKeys, SelectDefaultOptionsType } from '../types';
import { getFilterCondStorage, saveFilterCondStorage } from '../utils/storage';

/**
 * 获取cond接口数据，并整和组件传入的参数
 * @param filter      过滤器信息，用于获取请求的key
 * @param gameInfo    游戏信息
 * @param userInfo    用户信息
 * @param excludeKey  排除的key
 * @returns
 */
export default function useFilterCond({
  filter,
  gameInfo,
  userInfo,
  defaultOptions,
  backCondOriginData,
  otherRequestType = [],
  isNoRequest,
  externalCondData,
  dealwithFinallyCondData,
}: {
  filter: Array<FilterKeys>;
  gameInfo: any;
  userInfo: any;
  otherRequestType?: Array<string>;
  defaultOptions: SelectDefaultOptionsType;
  backCondOriginData?: (options: any) => void;
  isNoRequest?: boolean;
  /** 外部注入的 cond 数据，配合 isNoRequest=true 使用 */
  externalCondData?: any;
  dealwithFinallyCondData?: (options: any) => any;
}) {
  /** 组件中需要请求的选项 */
  const [filterOptions, setFilterOptions] = useState<{ [key: string]: any }>(
    {},
  );

  const [loading, setLoading] = useState(true);

  const saveCondRequest = (options: any) => {
    saveFilterCondStorage(gameInfo, options);
    backCondOriginData?.(options);
  };

  const getCondRequest = () => {
    const options = getFilterCondStorage(gameInfo);
    return options || {};
  };

  const getPromiseAllFunc = (
    requestType: Array<string>,
    requestFuncs: Array<(params: any) => Promise<any>>,
    params: any,
  ) => {
    const requestFuncList = [];
    if (requestType.length > 0) {
      requestFuncList.push(
        getOpinionFilterCond({
          request_type: requestType,
          un_request_type: ['topic_trans'],
          ...gameInfo,
          ...params,
        }),
      );
    }
    if (requestFuncs.length > 0) {
      requestFuncList.push(...requestFuncs.map(item => item(gameInfo)));
    }

    if (requestFuncList.length === 0) return {};

    return Promise.all(requestFuncList)
      .then(list => {
        return list.reduce((acc, cur) => {
          return { ...acc, ...(cur || {}) };
        }, {});
      })
      .catch((error: any) => {
        console.log(error);
        // notification.error({
        //   description: "获取cond数据失败",
        // });
        return {};
      });
  };

  const requestFilterConfig = async () => {
    setLoading(true);

    const response = getCondRequest() || {};
    const { keys: requestType, params } = getRequestType(
      filter,
      Object.keys(defaultOptions || {}),
      response,
      otherRequestType
    );
    const requestFuncs = getRequestFuncList(filter, response);
    // 没有需要请求的参数，就不请求，后续还需要对这个进行缓存，如果有的就不请求
    const newResponse = await getPromiseAllFunc(
      requestType,
      requestFuncs,
      params,
    );

    Object.assign(response, newResponse);
    let newCondData = response;
    if (dealwithFinallyCondData) {
      try {
        newCondData = dealwithFinallyCondData(
          JSON.parse(JSON.stringify(response)),
        );
      } catch (error) {
        newCondData = response;
      }
    }

    const options = filterOptionsAssemble(filter, newCondData, defaultOptions);
    setFilterOptions(options);
    saveCondRequest(response);
    setLoading(false);
  };

  useEffect(() => {
    if (isNoRequest) {
      // 外部注入 cond 数据模式
      if (externalCondData && Object.keys(externalCondData).length > 0) {
        let condData = externalCondData;
        if (dealwithFinallyCondData) {
          try {
            condData = dealwithFinallyCondData(
              JSON.parse(JSON.stringify(externalCondData)),
            );
          } catch (error) {
            condData = externalCondData;
          }
        }
        const options = filterOptionsAssemble(filter, condData, defaultOptions);
        setFilterOptions(options);
        backCondOriginData?.(externalCondData);
      }
      setLoading(false);
      return;
    }
    if (gameInfo?.edition_unified_id) {
      requestFilterConfig();
    }
  }, [gameInfo, externalCondData]);

  return { filterOptions, loading };
}
